/**
 * Real file bytes for upload endpoints (same helper as the web app's src/api/uploads.ts).
 *
 * The contracts describe an upload as JSON metadata (`UploadInput`, `{ name, sizeBytes, mimeType }`…): that is
 * all the mock needs. The live API also takes `multipart/form-data` on those endpoints: one part `payload` with
 * the JSON body (validated by the same contract) plus the file parts (contracts README § Files).
 *
 * Build upload metadata with `attachFile(meta, file)` (or the helpers that call it: `fileMeta`, `toUpload`…).
 * It returns the metadata object unchanged and remembers which `File` it describes. In live mode `call()`
 * walks the request body, and when it finds such objects it sends multipart with the files; in mock mode
 * nothing changes.
 */

const files = new WeakMap<object, Blob>();

/** Tie an upload's metadata object to the browser file it describes (no copy, no effect on the JSON). */
export function attachFile<T extends object>(meta: T, file: Blob): T {
  files.set(meta, file);
  return meta;
}

/** The browser file tied to `meta` by attachFile(), if any. */
export function fileOf(meta: object): Blob | undefined {
  return files.get(meta);
}

/** Copy the file tie of `from` onto a new metadata object (when a screen rebuilds the metadata before sending). */
export function withFileOf<T extends object>(meta: T, from: object): T {
  const f = files.get(from);
  return f ? attachFile(meta, f) : meta;
}

export interface FilePart {
  field: string;
  file: Blob;
  name: string;
}

/**
 * Every file referenced by `body`, with the multipart field name: the property holding the metadata
 * (`file`, `attachments`, `files`…), or `file` when the body itself is the metadata.
 */
export function collectFiles(body: unknown): FilePart[] {
  const out: FilePart[] = [];
  const seen = new Set<object>();
  const visit = (v: unknown, field: string) => {
    if (!v || typeof v !== "object" || seen.has(v)) return;
    seen.add(v);
    const f = files.get(v);
    if (f) {
      const meta = v as { name?: unknown; fileName?: unknown };
      const name = typeof meta.name === "string" ? meta.name : typeof meta.fileName === "string" ? meta.fileName : f instanceof File ? f.name : "fichier";
      out.push({ field, file: f, name });
      return;
    }
    if (Array.isArray(v)) for (const x of v) visit(x, field);
    else for (const [k, x] of Object.entries(v)) visit(x, k);
  };
  visit(body, "file");
  return out;
}
